"use client"
import { useContext } from "react"

import { AuthUIContext } from "../../../lib/auth-ui-provider"
import { cn } from "@papier/ui/lib/utils"
import type { AuthLocalization } from "../../../localization/auth-localization"
import { CardContent } from "@papier/ui/components/ui/card"
import { Button } from "@papier/ui/components/ui/button"
import { getLocalizedError } from "../../../lib/utils"
import type { SettingsCardClassNames } from "../shared/settings-card"
import { SettingsCard } from "../shared/settings-card"
import { SettingsCellSkeleton } from "../skeletons/settings-cell-skeleton"
import { SessionCell } from "./session-cell"

export interface SessionsCardProps {
    className?: string
    classNames?: SettingsCardClassNames
    localization?: Partial<AuthLocalization>
}

export function SessionsCard({
    className,
    classNames,
    localization
}: SessionsCardProps) {
    const {
        hooks: { useListSessions },
        localization: contextLocalization,
        basePath,
        viewPaths,
        navigate
    } = useContext(AuthUIContext)

    localization = { ...contextLocalization, ...localization }

    const { data: sessions, isPending, refetch, error } = useListSessions()
    // TanStack throws BetterFetchError (body in `error`); the default hooks
    // expose the response body directly. Handle both without changing auth policy.
    const errorBody: unknown = error && "error" in error ? error.error : error
    const requiresSignIn =
        typeof errorBody === "object" && errorBody !== null &&
        "code" in errorBody && errorBody.code === "SESSION_NOT_FRESH"

    return (
        <SettingsCard
            className={className}
            classNames={classNames}
            description={localization.SESSIONS_DESCRIPTION}
            isPending={isPending}
            title={localization.SESSIONS}
        >
            <CardContent className={cn("grid gap-4", classNames?.content)}>
                {isPending ? (
                    <SettingsCellSkeleton classNames={classNames} />
                ) : error ? (
                    <div className="grid justify-items-start gap-3" role="alert">
                        <p className="text-muted-foreground text-sm">
                            {requiresSignIn
                                ? localization.SESSION_NOT_FRESH
                                : getLocalizedError({ error, localization })}
                        </p>
                        <Button
                            variant="outline"
                            onClick={() =>
                                requiresSignIn
                                    ? navigate(`${basePath}/${viewPaths.SIGN_OUT}`)
                                    : void refetch?.()
                            }
                        >
                            {requiresSignIn
                                ? localization.SIGN_OUT
                                : localization.CONTINUE}
                        </Button>
                    </div>
                ) : (
                    sessions?.map((session) => (
                        <SessionCell
                            key={session.id}
                            classNames={classNames}
                            localization={localization}
                            session={session}
                            refetch={refetch}
                        />
                    ))
                )}
            </CardContent>
        </SettingsCard>
    )
}
